import {useCallback, useSyncExternalStore} from 'react';
import {LocalStorage} from '../../utils/LocalStorage';

// Lo que el usuario ya resolvió en pantalla —descartó una campaña— hasta que el backend lo refleje. Vive acá
// y no en cada chip porque la MISMA campaña se ve en varios lugares a la vez: el chip de la línea y el de cada
// cuenta (el aviso «sin línea» y el popover de la cuenta de la tanda del 16/10 se fueron con UD-5). Lo que se
// resuelve en uno tiene que salir de todos, también de la fuga de OTRA línea que la nombre (review
// C-cl-fe-linea-05).
//
// Una marca por campaña (`campaignKey`) para toda la pestaña, con la hora en que se marcó. La marca se ata al
// DATO y no al reloj (review C-cs-fe-01): descuenta la campaña de todo dato calculado ANTES de marcarla
// (`computed_on`), que es justo el que todavía no lo sabe; un dato calculado después ya es la verdad del
// backend (descuenta los descartes al leer y recalcula en segundos después de sumar, contrato §3.1 y §4).
// Con un plazo fijo, la marca vencía sobre un Tablero abierto que nunca volvía a pedir el dato, y lo resuelto
// reaparecía en un momento cualquiera. «Deshacer» la borra; recargar la página, también.
//
// Las marcas son de la SESIÓN que las hizo (review C-cs-fe-02): el logout no recarga la página, y la marca de
// una agencia no puede esconderle una campaña a otra que entre en la misma pestaña con la misma cuenta. Mismo
// motivo por el que la clave del cache de `APIClient` lleva el token.

const EMPTY = Object.freeze({});
const byCampaign = new Map();
const listeners = new Set();
let snapshot = EMPTY;
let owner = null;

/** La sesión de quien mira: el token. Una marca de otra sesión no vale. */
export function currentSession() {
    try {
        return LocalStorage.fetchUserToken() || null;
    } catch (error) {
        return null;
    }
}

function rebuild() {
    const resolved = {};
    byCampaign.forEach((markedAt, key) => { resolved[key] = markedAt; });
    snapshot = Object.keys(resolved).length ? Object.freeze(resolved) : EMPTY;
}

// Cambió la sesión (logout + login sin recargar): las marcas de la anterior se tiran.
function syncOwner() {
    const session = currentSession();
    if (session === owner) return;
    owner = session;
    byCampaign.clear();
    snapshot = EMPTY;
}

function emit() {
    rebuild();
    listeners.forEach((listener) => listener());
}

function subscribe(listener) {
    listeners.add(listener);
    return () => listeners.delete(listener);
}

/** `{[campaignKey]: markedAt}` de la sesión actual. Conserva la identidad mientras no cambie nada. */
export function currentResolutions() {
    syncOwner();
    return snapshot;
}

/**
 * Marca resueltas esas campañas. `session` es la sesión que hizo el pedido: si cambió mientras volvía la
 * respuesta, no se marca nada.
 */
export function markResolved(keys, {now = Date.now(), session = currentSession()} = {}) {
    if (!keys || !keys.length) return;
    syncOwner();
    if (session !== owner) return;
    keys.forEach((key) => byCampaign.set(key, now));
    emit();
}

export function unmarkResolved(keys) {
    if (!keys || !keys.length) return;
    syncOwner();
    keys.forEach((key) => byCampaign.delete(key));
    emit();
}

/** Para los tests: cada uno arranca sin nada resuelto. */
export function resetResolutions() {
    byCampaign.clear();
    owner = currentSession();
    emit();
}

/** Lo resuelto en la pestaña, `{[campaignKey]: markedAt}`, para `withResolutions`. */
export function useUncoveredResolutions() {
    return useSyncExternalStore(subscribe, currentResolutions);
}

/** Para los componentes que sólo marcan: no hace falta suscribirse al store (review C-cs-fe-13). */
export function useResolutionActions() {
    const mark = useCallback((keys, session) => markResolved(keys, {session}), []);
    const unmark = useCallback((keys) => unmarkResolved(keys), []);
    return {mark, unmark};
}
